import { useDuplicateOrderQuoteMutation } from '@codezo/graphql';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { OrganizationSelector } from '../../selectors/OrganizationSelector';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';

export interface DuplicateQuoteSource {
	id: string;
	name: string;
	organizationId?: string | null;
	organizationName?: string | null;
}

interface DuplicateOrderQuoteModalProps {
	/** The quote being copied — `null` keeps the dialog closed. */
	quote: DuplicateQuoteSource | null;
	onOpenChange: (open: boolean) => void;
	/** Fired with the new quote's id once the copy exists. */
	onDuplicated: (newQuoteId: string) => void;
}

/**
 * Copies a quote into a fresh DRAFT. The backend carries the products (with their
 * quantity tiers, files and specification snapshot), the request lines and — unless
 * turned off — the offer batches. None of the original's history comes along.
 */
export function DuplicateOrderQuoteModal({
	quote,
	onOpenChange,
	onDuplicated,
}: DuplicateOrderQuoteModalProps) {
	const open = !!quote;
	const [name, setName] = useState('');
	const [organizationId, setOrganizationId] = useState('');
	const [organizationLabel, setOrganizationLabel] = useState<string | undefined>(undefined);
	const [includeOffers, setIncludeOffers] = useState(true);

	const [duplicateOrderQuote, { loading }] = useDuplicateOrderQuoteMutation();

	/* Reseed once per opening, so a previous copy's answers never carry into the
	   next one. Tracked by id rather than by the `quote` object: callers build it
	   inline, so a parent re-render would otherwise wipe what has been typed. */
	const seededFor = useRef<string | null>(null);
	useEffect(() => {
		if (!quote) {
			seededFor.current = null;
			return;
		}
		if (seededFor.current === quote.id) return;
		seededFor.current = quote.id;
		setName(`${quote.name} (copy)`);
		setOrganizationId(quote.organizationId ?? '');
		setOrganizationLabel(quote.organizationName ?? undefined);
		setIncludeOffers(true);
	}, [quote]);

	const clientChanged = !!quote?.organizationId && organizationId !== quote.organizationId;

	const handleSubmit = async () => {
		if (!quote || !name.trim() || !organizationId) return;

		try {
			const { data } = await duplicateOrderQuote({
				variables: {
					id: quote.id,
					input: {
						name: name.trim(),
						// Only sent when it actually moves — omitting it keeps the original's client.
						organizationId: clientChanged ? organizationId : undefined,
						includeOffers,
					},
				},
			});

			const res = data?.duplicateOrderQuote;
			if (res?.success && res.referenceKey) {
				toast.success('Quote duplicated');
				onOpenChange(false);
				onDuplicated(res.referenceKey);
			} else if (res?.success) {
				// Copied but no id returned — can't open it.
				toast.error('Quote duplicated but could not be opened');
				onOpenChange(false);
			} else {
				toast.error(res?.message || 'Failed to duplicate quote');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to duplicate quote');
		}
	};

	const canSubmit = !!name.trim() && !!organizationId && !loading;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Duplicate quote</DialogTitle>
					<DialogDescription>
						Creates a new draft with the same products, quantity tiers and files. No
						activity, guest links, view counts or acceptance come along.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label>
									Name <span className="text-destructive">*</span>
								</Label>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="Name for the copy"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>
									Client <span className="text-destructive">*</span>
								</Label>
								<OrganizationSelector
									value={organizationId}
									onChange={(v, label) => {
										setOrganizationId(v);
										setOrganizationLabel(label);
									}}
									selectedLabel={organizationLabel}
									placeholder="Search clients…"
									skip={!open}
								/>
								{clientChanged && (
									<div className="text-xs text-text-4">
										The contact list, delivery address and payment settings
										belong to the client, so they are taken from{' '}
										{organizationLabel || 'the new client'} rather than copied.
									</div>
								)}
							</div>

							<div className="flex items-center justify-between gap-4 border-t border-line-1 pt-4">
								<div className="min-w-0">
									<div className="text-sm font-medium text-text-1">
										Copy offers
									</div>
									<div className="text-xs text-text-4">
										Offer batches land in draft — re-pricing and re-sending
										stays an explicit act.
									</div>
								</div>
								<Switch
									checked={includeOffers}
									onCheckedChange={setIncludeOffers}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit}>
							{loading ? 'Duplicating…' : 'Duplicate quote'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
